import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { useAuth } from "react-oidc-context";
import { useActiveDatabase, useDatabases, useOnDatabaseChange } from "@/lib/useDatabases";
import { downloadTraceExport, exportOfAnswer, exportOfRecord } from "@/lib/traceExport";
import {
  changeWords,
  changesBetweenRuns,
  firstLine,
  groupRuns,
  type QuestionSeries,
  type SiteChange,
} from "@/lib/questionSeries";
import { CelebrationBurst } from "@/components/CelebrationBurst";
import { Markdown } from "@/components/Markdown";
import {
  AnswerTokenomics,
  TokenomicsPanel,
} from "@/components/TokenomicsPanel";
import {
  aiChatStreaming,
  appendEvalQuestion,
  cancelEvalRun,
  createEvalBank,
  deleteEvalBank,
  createCorrection,
  deleteCorrection,
  downloadBankCorpus,
  downloadTrainingDataset,
  downloadTrainingSet,
  listCorrections,
  type Correction,
  getEvalBank,
  getEvalRun,
  getRunRecommendations,
  gradeEvalResult,
  gradeQuickQuestion,
  listEvalBanks,
  listEvalRuns,
  fetchQuickQuestionTrace,
  fetchSiteChanges,
  listQuickQuestions,
  replaceEvalQuestions,
  startEvalRun,
  updateEvalBank,
  type AiChatResponse,
  type AiChatTraceStep,
  type ApiResult,
  type BankDetail,
  type BankSummary,
  type EvalMode,
  type EvalQuestion,
  type EvalQuestionRef,
  type EvalRecommendation,
  type HumanGrade,
  type QuestionType,
  type QuickQuestionRow,
  type RunDetail,
  type RunListRow,
  type RunRecommendationsResponse,
  type RunResultRow,
} from "@/lib/api";
import { ALL_GRAPHS } from "@/components/GraphSelector";
import {
  canImportData,
  canReviewAllQuestions,
  isGuest,
  useMe,
} from "@/lib/useMe";
import { cn } from "@/lib/utils";
import { profileSpreadsheet, rdfProfileSpreadsheet } from "@/lib/api";
import { isQros } from "@/lib/useMe";
import {
  isSheetFile,
  parseQuestionsFile,
  questionsFromTable,
} from "@/lib/questionFile";
import { buttonVariants } from "@/components/ui/Button";
import { Message } from "@/components/ui/Message";
import { EntityPicker } from "@/components/EntityPicker";
import type { UriLabel } from "@/lib/useEntity";

type TopMode = "quick" | "bank" | "training";

// Shown as a PLACEHOLDER, never as the field's value. Prefilled text is text
// the user has to delete before they can type their own question, and a sample
// left in the box is a question somebody eventually asks by accident.
const EXAMPLE_QUESTION = "How many concepts are in the vocabulary?";

export function AiChatPage() {
  const [topMode, setTopMode] = useState<TopMode>("quick");
  // Bank runs and training-corpus downloads are authenticated surfaces: a guest
  // gets refused by every endpoint behind them. Offering the tabs anyway is the
  // same defect as the empty history panel -- a control that leads nowhere.
  // Asking a question is the part a signed-out reviewer is here to do.
  const meData = useMe().data;
  const guest = isGuest(meData);

  return (
    <div className="container mx-auto py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">LLM Testbed</h1>
        {!guest && <ModeToggle value={topMode} onChange={setTopMode} />}
      </div>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        Tests an ontology and/or dataset against a consistent cloud-based LLM.
        Useful for determining whether the ontology and data have sufficient
        coverage and depth for your use case.
      </p>
      {/* Which model produced the answers. The model is a per-instance choice an
          admin can change, so the same question can be answered by different
          models on different days -- and it is the first thing anyone debugging a
          bad answer needs. Read from /api/me so every role sees it; the admin
          LLM endpoint requires ServerSettings. */}
      <p className="mt-1 text-xs text-muted-foreground">
        {meData?.ai_model
          ? `Answering with ${meData.ai_provider ?? "model"} · ${meData.ai_model}`
          : "No model is configured on this instance, so questions cannot be answered."}
      </p>

      {(guest || topMode === "quick") && <QuickPanel />}
      {!guest && topMode === "bank" && <EvaluatorPanel />}
      {!guest && topMode === "training" && <TrainingPanel />}
    </div>
  );
}

/** Trainer P3 — generate offline training datasets from a graph's declared
 *  ontology: classification fine-tune corpus (chat JSONL / Ollama Modelfile)
 *  and NL→QQL question/query pairs. */
function TrainingPanel() {
  const auth = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const downloadVerified = async () => {
    const token = auth.user?.id_token;
    if (!token) return;
    setBusy("verified");
    setMsg(null);
    const r = await downloadTrainingSet(token);
    setBusy(null);
    if (!r.ok) setMsg(r.message);
  };

  const download = async (format: "jsonl" | "modelfile" | "qql") => {
    const token = auth.user?.id_token;
    if (!token) return;
    setBusy(format);
    setMsg(null);
    // The LLM surface is cross-graph by design (GRAPH_SELECTION_SURFACES.md):
    // generation reads the declared schema across every graph, so the endpoint
    // — which requires a graph — is sent the union explicitly.
    const r = await downloadTrainingDataset(ALL_GRAPHS, format, token);
    setBusy(null);
    if (!r.ok) setMsg(r.message);
  };

  const options: Array<["jsonl" | "modelfile" | "qql", string, string]> = [
    [
      "jsonl",
      "Classification corpus (.jsonl)",
      "Chat-format fine-tuning pairs: entity properties → class, built from the declared schema",
    ],
    [
      "modelfile",
      "Ollama Modelfile",
      "Ready-to-create Ollama model with the system prompt and example exchanges baked in",
    ],
    [
      "qql",
      "NL→QQL pairs (.jsonl)",
      "Question/query pairs for tuning a model to write QQL against this schema",
    ],
  ];

  return (
    <>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Two sources of training material you own: the answers your team
          verified, and datasets generated from the declared ontology across
          every graph. Nothing here retrains the assistant by itself — a
          download is yours to apply to any model.
        </p>
      </div>

      <div className="mt-6 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-4">
          <div className="min-w-0">
            <div className="text-sm font-medium">Verified answers (.jsonl)</div>
            <p className="text-xs text-muted-foreground">
              Every answer a person graded pass — question-set runs, plus the
              quick questions you can see — as chat-format fine-tuning pairs.
              Grading a quick question pass puts it here.
            </p>
          </div>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void downloadVerified()}
            className={buttonVariants({ variant: "primary", size: "lg" })}
          >
            {busy === "verified" ? "Preparing…" : "Download"}
          </button>
        </div>
        {options.map(([format, label, help]) => (
          <div
            key={format}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-4"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium">{label}</div>
              <p className="text-xs text-muted-foreground">{help}</p>
            </div>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => void download(format)}
              className={buttonVariants({ variant: "primary", size: "lg" })}
            >
              {busy === format ? "Generating…" : "Download"}
            </button>
          </div>
        ))}
        {msg && (
          <div className="rounded border border-destructive bg-destructive/10 p-2 text-xs text-destructive">
            {msg}
          </div>
        )}
      </div>
    </>
  );
}

/** Trainer P4 — teach the assistant. Corrections are guidance the grounding
 *  injects into every chat and eval run for this tenant: visible here,
 *  revocable by delete, never written into the data, and every add and
 *  removal is in the audit log. Tenant-wide, because the LLM surface reads
 *  across graphs — a graph-scoped correction would apply to a scope this page
 *  does not have. Lives on the quick-question panel, where the answer that
 *  needs correcting was just read. */
function CorrectionsManager() {
  const auth = useAuth();
  const me = useMe();
  const [rows, setRows] = useState<Correction[]>([]);
  const [kind, setKind] = useState<"alias" | "clarification">("alias");
  const [phrase, setPhrase] = useState("");
  const [target, setTarget] = useState<UriLabel | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const reload = async () => {
    const token = auth.user?.id_token;
    if (!token) return;
    // No graph argument: list every correction in force for this tenant.
    const r = await listCorrections(token);
    if (r.ok) setRows(r.data.corrections);
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user?.id_token]);

  if (!canImportData(me.data)) return null;

  const add = async () => {
    const token = auth.user?.id_token;
    if (!token) return;
    setBusy(true);
    setMsg(null);
    const r = await createCorrection(
      {
        kind,
        ...(kind === "alias"
          ? { phrase: phrase.trim(), target: target?.uri ?? "" }
          : { note: note.trim() }),
      },
      token,
    );
    setBusy(false);
    if (r.ok) {
      setPhrase("");
      setTarget(null);
      setNote("");
      await reload();
    } else {
      setMsg(r.message);
    }
  };

  const remove = async (id: string) => {
    const token = auth.user?.id_token;
    if (!token) return;
    const r = await deleteCorrection(id, token);
    if (!r.ok) setMsg(r.message);
    await reload();
  };

  const canAdd =
    kind === "alias"
      ? phrase.trim() !== "" && target !== null
      : note.trim() !== "";

  return (
    <div className="mt-8">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Guide the assistant
      </div>
      <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
        Corrections are injected into every AI answer as guidance — an alias
        maps your team's wording to the right term; a clarification is a
        standing instruction. They never change the data and can be removed here
        at any time.
      </p>

      <div className="mt-3 space-y-2 rounded-md border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={kind}
            onChange={(e) =>
              setKind(e.target.value as "alias" | "clarification")
            }
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          >
            <option value="alias">Alias (phrase → term)</option>
            <option value="clarification">Clarification (instruction)</option>
          </select>
        </div>
        {kind === "alias" ? (
          <div className="flex flex-wrap gap-2">
            <input
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder="Your team's phrase, e.g. “the war chest”"
              className="min-w-[220px] flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            />
            <span className="self-center text-xs text-muted-foreground">
              refers to
            </span>
            <div className="min-w-[220px] flex-1">
              <EntityPicker
                selected={target}
                onSelect={setTarget}
                placeholder="Search for the term by its name"
                label="The term the phrase refers to"
              />
            </div>
          </div>
        ) : (
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Standing instruction, e.g. “Quarters are fiscal (Oct–Sep), not calendar.”"
            className="block w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          />
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={busy || !canAdd}
            onClick={() => void add()}
            className={buttonVariants({ variant: "primary", size: "md" })}
          >
            {busy ? "Saving…" : "Add correction"}
          </button>
          {msg && <span className="text-xs text-destructive">{msg}</span>}
        </div>
      </div>

      {rows.length > 0 && (
        <ul className="mt-3 divide-y divide-border rounded-md border border-border bg-card">
          {rows.map((c) => (
            <li
              key={c.id}
              className="flex items-center gap-3 px-4 py-2 text-sm"
            >
              <span
                className={cn(
                  "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase",
                  c.kind === "alias"
                    ? "bg-tres-info/15 text-tres-info"
                    : "bg-tres-warning/15 text-tres-warning",
                )}
              >
                {c.kind}
              </span>
              <span className="min-w-0 flex-1 truncate">
                {c.kind === "alias" ? (
                  <>
                    “{c.phrase}” →{" "}
                    <span title={c.target}>{c.target_label ?? c.target}</span>
                  </>
                ) : (
                  c.note
                )}
                {c.graph && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({c.graph})
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => void remove(c.id)}
                className={cn(
                  buttonVariants({ variant: "ghost", size: "icon" }),
                  "shrink-0",
                )}
                title="Remove this correction"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ModeToggle({
  value,
  onChange,
}: {
  value: TopMode;
  onChange: (v: TopMode) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border bg-card p-0.5 text-sm">
      <button
        type="button"
        onClick={() => onChange("quick")}
        className={cn(
          "rounded px-3 py-1.5 font-medium transition-colors",
          value === "quick"
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        Quick question
      </button>
      <button
        type="button"
        onClick={() => onChange("bank")}
        className={cn(
          "rounded px-3 py-1.5 font-medium transition-colors",
          value === "bank"
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        Run a question set
      </button>
      <button
        type="button"
        onClick={() => onChange("training")}
        className={cn(
          "rounded px-3 py-1.5 font-medium transition-colors",
          value === "training"
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        Training data
      </button>
    </div>
  );
}

// =================================================================
// Quick question (single-shot) — original Trainer behavior
// =================================================================

function QuickPanel() {
  const auth = useAuth();
  // Recent questions is a REVIEW surface: other users' questions, with grading.
  // A guest can neither be attributed nor grade, and the endpoint refuses them,
  // so the control would expand onto nothing. An element that does nothing is
  // worse than an absent one.
  const meData = useMe().data;
  const guest = isGuest(meData);
  const databases = useDatabases();
  const { active } = useActiveDatabase();
  const activeDb = active ?? databases.data?.default ?? "main";
  const activeDbName =
    databases.data?.databases.find((d) => d.id === activeDb)?.name ?? activeDb;
  const modelName =
    meData?.ai_provider && meData.ai_model ? `${meData.ai_provider}:${meData.ai_model}` : null;

  const [question, setQuestion] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ApiResult<AiChatResponse> | null>(null);
  // What the turn is doing right now. A grounded answer retrieves in several
  // rounds and can run for minutes; the surface used to show one motionless
  // label for all of it, which reads as hung.
  const [progress, setProgress] = useState<string[]>([]);
  // An answer on screen belongs to the database that gave it.
  useOnDatabaseChange(
    useCallback(() => {
      setResult(null);
      setProgress([]);
    }, []),
  );

  const handleAsk = async () => {
    // No local auth gate. A signed-out visitor on an instance that serves them
    // holds AiChat, bounded server-side by a per-IP daily budget; a client-side
    // 401 here made that unreachable and told the user something untrue.
    const token = auth.user?.id_token;
    const trimmed = question.trim();
    if (!trimmed) return;

    setRunning(true);
    setResult(null);
    setProgress([]);
    try {
      const r = await aiChatStreaming({ message: trimmed }, token, (text) =>
        setProgress((lines) => [...lines, text]),
      );
      setResult(r);
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Ask a single question in plain language. The model never writes a
          query — it retrieves facts through the engine&apos;s grounded tools
          and answers only from what comes back, with a trace of every step.
          Useful for exploration.
        </p>
      </div>

      <div className="mt-4">
        <label className="block">
          <span className="text-sm font-medium">Question</span>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={EXAMPLE_QUESTION}
            disabled={running}
            spellCheck={true}
            rows={3}
            className={cn(
              "mt-1 block w-full rounded-md border border-input bg-background px-3 py-2",
              "text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring",
              "disabled:opacity-50",
            )}
          />
        </label>

        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={handleAsk}
            disabled={running || question.trim().length === 0}
            className={cn(
              "rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground",
              "hover:bg-primary/90 disabled:opacity-50",
            )}
          >
            {running ? "Thinking…" : "Ask"}
          </button>
          <span className="text-xs text-muted-foreground">
            POST /api/v1/ai/chat — the model retrieves facts from the graph to
            ground its answer.
          </span>
        </div>

        {running && progress.length > 0 && (
          <ol className="mt-3 space-y-1 text-xs text-muted-foreground">
            {progress.map((line, i) => (
              <li
                key={`${i}-${line}`}
                className={cn(i === progress.length - 1 && "font-medium")}
              >
                {line}
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="mt-8">
        <QuickResultPanel
          result={result}
          question={question.trim()}
          database={activeDbName}
          model={modelName}
        />
        {result?.ok && (
          <AddToBankFlow
            question={question.trim()}
            answer={result.data.answer}
          />
        )}
        {/* A grade is recorded for a signed-in person; the server refuses a guest's. */}
        {result?.ok && result.data.question_id && !guest && (
          <QuickGradeBar
            key={result.data.question_id}
            questionId={result.data.question_id}
            grade={null}
            notes=""
          />
        )}
      </div>

      {!guest && (
        <QuickHistory
          latestQuestionId={result?.ok ? result.data.question_id : undefined}
        />
      )}
      {!guest && <CorrectionsManager />}
    </>
  );
}

/** Pass/partial/fail + notes for a persisted quick question — the same
 *  grading semantics as an eval-run result, applied in place. */
function QuickGradeBar({
  questionId,
  grade,
  notes,
  onChange,
}: {
  questionId: string;
  grade: HumanGrade | null;
  notes: string;
  onChange?: (grade: HumanGrade | null, notes: string) => void;
}) {
  const auth = useAuth();
  const [current, setCurrent] = useState<HumanGrade | null>(grade);
  const [noteText, setNoteText] = useState(notes);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const save = async (nextGrade: HumanGrade | null, nextNotes: string) => {
    const token = auth.user?.id_token;
    if (!token) return;
    setSaving(true);
    setSaveError(null);
    const body: { human_grade: HumanGrade | null; human_grade_notes?: string } =
      {
        human_grade: nextGrade,
      };
    if (nextNotes) body.human_grade_notes = nextNotes;
    const r = await gradeQuickQuestion(questionId, body, token);
    setSaving(false);
    if (r.ok) {
      setCurrent(nextGrade);
      onChange?.(nextGrade, nextNotes);
    } else {
      // A swallowed failure reads as "the click didn't register" — say it.
      setSaveError("Grade failed to save — retry");
    }
  };

  return (
    <div className="mt-3 rounded-md border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        {saveError && (
          <span role="alert" className="text-xs text-destructive">
            {saveError}
          </span>
        )}
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Grade this answer
        </span>
        {(["pass", "partial", "fail"] as HumanGrade[]).map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => void save(g, noteText)}
            disabled={saving}
            className={cn(
              "rounded px-2 py-1 text-xs font-medium border",
              current === g
                ? g === "pass"
                  ? "border-tres-status-active bg-tres-status-active text-primary-foreground"
                  : g === "fail"
                    ? "border-destructive bg-destructive text-primary-foreground"
                    : "border-tres-warning bg-tres-warning text-primary-foreground"
                : "border-border hover:bg-muted",
            )}
          >
            {g}
          </button>
        ))}
        {current && (
          <button
            type="button"
            onClick={() => void save(null, noteText)}
            className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
            title="Ungrade"
          >
            ×
          </button>
        )}
      </div>
      <textarea
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        onBlur={() => {
          if (noteText !== notes) void save(current, noteText);
        }}
        rows={1}
        placeholder="Why this grade? Grade against the store: an honest “the data doesn't say” is a pass when true."
        className="mt-2 block w-full rounded border border-input bg-background px-2 py-1 text-xs"
      />
    </div>
  );
}

/** Recent quick questions — the review surface that makes ad-hoc results
 *  visible and gradeable after the fact. The server scopes the rows: a
 *  user's own, or every user's for Owner, Admin and Auditor. */
function QuickHistory({ latestQuestionId }: { latestQuestionId: string | undefined }) {
  const auth = useAuth();
  const reviewAll = canReviewAllQuestions(useMe().data);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<QuickQuestionRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = async () => {
    const token = auth.user?.id_token;
    if (!token) return;
    const r = await listQuickQuestions(token, 200);
    if (r.ok) {
      setRows(r.data);
      setLoadError(null);
    } else {
      setRows([]);
      setLoadError(
        r.status === 503
          ? "History unavailable — no state store on this deployment."
          : r.message,
      );
    }
  };

  useEffect(() => {
    if (open && rows === null) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // The list is the database's; a switch reloads it or forgets it.
  useOnDatabaseChange(
    useCallback(() => {
      setRows(null);
      setLoadError(null);
      if (open) void load();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]),
  );

  // An answer just recorded belongs in an open list without a press on Refresh.
  useEffect(() => {
    if (open && latestQuestionId) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestQuestionId]);

  return (
    <div className="mt-10">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-sm font-medium underline-offset-2 hover:underline"
        >
          {open ? "▾" : "▸"}{" "}
          {reviewAll ? "Recent questions (all users)" : "Your recent questions"}
        </button>
        {open && (
          <button
            type="button"
            onClick={() => void load()}
            className="rounded border border-border px-2 py-0.5 text-xs hover:bg-muted"
          >
            Refresh
          </button>
        )}
      </div>
      {open && loadError && (
        <div className="mt-2 text-xs text-muted-foreground">{loadError}</div>
      )}
      {open && rows !== null && rows.length === 0 && !loadError && (
        <div className="mt-2 text-xs text-muted-foreground">
          No quick questions recorded yet.
        </div>
      )}
      {open && rows !== null && rows.length > 0 && (
        <ul className="mt-3 space-y-3">
          {groupRuns(rows).map((series) => (
            <QuickHistoryRow key={series.key} series={series} onRan={() => void load()} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** The runs of one question before the latest run's answer: each with its
 *  date, model, grade and the first line of its answer, and between two
 *  runs what changed on the site that could have changed the answer. */
function RunSeries({ series }: { series: QuestionSeries }) {
  const auth = useAuth();
  const [changes, setChanges] = useState<SiteChange[] | null>(null);
  const [changesError, setChangesError] = useState<string | null>(null);
  const runs = series.runs;
  const newest = series.latest;
  const oldest = runs[runs.length - 1] ?? newest;

  useEffect(() => {
    const token = auth.user?.id_token;
    if (!token || runs.length < 2) return;
    let live = true;
    void fetchSiteChanges(oldest.asked_at, newest.asked_at, token).then((r) => {
      if (!live) return;
      if (r.ok) setChanges(r.data);
      else setChangesError(r.message);
    });
    return () => {
      live = false;
    };
  }, [auth.user?.id_token, oldest.asked_at, newest.asked_at, runs.length]);

  const between = changesBetweenRuns(runs, changes ?? []);
  return (
    <div className="mt-2 rounded border border-border bg-background p-2 text-xs">
      <div className="font-medium text-muted-foreground">
        {runs.length} runs of this question, newest first
      </div>
      <ol className="mt-1 space-y-1">
        {runs.map((run, i) => {
          const gap = between.get(run.id);
          return (
            <li key={run.id}>
              {i > 0 && changes && gap && (
                <div className="mb-1 pl-3 text-muted-foreground">
                  {gap.length === 0
                    ? "Nothing on the site changed between these runs."
                    : `Between these runs: ${gap.map(changeWords).join("; ")}.`}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <span>{new Date(run.asked_at).toLocaleString()}</span>
                {run.model_provider && (
                  <span className="text-muted-foreground">
                    {run.model_provider}:{run.model_name}
                  </span>
                )}
                <span className={cn("rounded px-1.5 py-0.5 font-medium", gradeChipClass(run.human_grade))}>
                  {run.human_grade ?? "ungraded"}
                </span>
                <span className="text-muted-foreground">{firstLine(run.answer_text) || "(no answer)"}</span>
              </div>
            </li>
          );
        })}
      </ol>
      {changesError && (
        <div className="mt-1 text-destructive">The site's changes could not be read: {changesError}</div>
      )}
    </div>
  );
}

function gradeChipClass(grade: HumanGrade | null): string {
  return grade === "pass"
    ? "bg-tres-status-active/15 text-tres-status-active"
    : grade === "fail"
      ? "bg-destructive/15 text-destructive"
      : grade === "partial"
        ? "bg-tres-warning/15 text-tres-warning"
        : "bg-muted text-muted-foreground";
}

function QuickHistoryRow({
  series,
  onRan,
}: {
  series: QuestionSeries;
  onRan: () => void;
}) {
  const row = series.latest;
  const auth = useAuth();
  const databases = useDatabases();
  const [expanded, setExpanded] = useState(false);
  const [showTokenomics, setShowTokenomics] = useState(false);
  const [grade, setGrade] = useState<HumanGrade | null>(row.human_grade);
  const [exportError, setExportError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<string[]>([]);
  const [runError, setRunError] = useState<string | null>(null);
  const databaseName =
    databases.data?.databases.find((d) => d.id === row.db_id)?.name ?? row.db_id;

  // The same question, asked again through the same path, so the new run is
  // recorded like any other and joins the series.
  const runAgain = async () => {
    const token = auth.user?.id_token;
    setRunning(true);
    setRunError(null);
    setProgress([]);
    try {
      const r = await aiChatStreaming(
        { message: row.question_text, ...(row.graph ? { graph: row.graph } : {}) },
        token,
        (text) => setProgress((lines) => [...lines, text]),
      );
      if (r.ok) onRan();
      else setRunError(r.message);
    } finally {
      setRunning(false);
    }
  };

  const exportTrace = async () => {
    const token = auth.user?.id_token;
    if (!token) return;
    setExportError(null);
    const r = await fetchQuickQuestionTrace(row.id, token);
    if (r.ok) downloadTraceExport(exportOfRecord(r.data, databaseName));
    else setExportError(r.message);
  };

  const gradeChip = gradeChipClass(grade);

  return (
    <li className="rounded-md border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{row.asked_by_name}</span>
        <span>{new Date(row.asked_at).toLocaleString()}</span>
        <span>answered from {databaseName}</span>
        {row.model_provider && (
          <span>
            {row.model_provider}:{row.model_name}
          </span>
        )}
        {row.latency_ms != null && (
          <span>{(row.latency_ms / 1000).toFixed(1)}s</span>
        )}
        <span className={cn("rounded px-1.5 py-0.5 font-medium", gradeChip)}>
          {grade ?? "ungraded"}
        </span>
        {series.runs.length > 1 && (
          <span className="rounded bg-muted px-1.5 py-0.5 font-medium">
            {series.runs.length} runs
          </span>
        )}
      </div>
      <div className="mt-1 text-sm font-medium">{row.question_text}</div>
      {series.runs.length > 1 && <RunSeries series={series} />}
      <div
        className={cn(
          "mt-1 whitespace-pre-wrap text-sm",
          !expanded && "line-clamp-3",
        )}
      >
        {row.answer_text || (
          <span className="italic text-muted-foreground">(no answer)</span>
        )}
      </div>
      <div className="mt-1 flex gap-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-muted-foreground underline hover:text-foreground"
        >
          {expanded ? "Collapse" : "Expand"}
        </button>
        <button
          type="button"
          aria-expanded={showTokenomics}
          onClick={() => setShowTokenomics((v) => !v)}
          className="text-xs text-muted-foreground underline hover:text-foreground"
        >
          Tokenomics
        </button>
        <button
          type="button"
          onClick={() => void exportTrace()}
          className="text-xs text-muted-foreground underline hover:text-foreground"
        >
          Download trace
        </button>
        <button
          type="button"
          onClick={() => void runAgain()}
          disabled={running}
          className="text-xs text-muted-foreground underline hover:text-foreground disabled:no-underline disabled:opacity-60"
        >
          {running ? "Running…" : "Run again"}
        </button>
      </div>
      {running && progress.length > 0 && (
        <ol className="mt-1 space-y-0.5 text-xs text-muted-foreground">
          {progress.map((line, i) => (
            <li key={`${i}-${line}`} className={cn(i === progress.length - 1 && "font-medium")}>
              {line}
            </li>
          ))}
        </ol>
      )}
      {runError && <div className="mt-1 text-xs text-destructive">{runError}</div>}
      {exportError && (
        <div className="mt-1 text-xs text-destructive">{exportError}</div>
      )}
      {showTokenomics && <TokenomicsPanel row={row} />}
      {expanded && (
        <QuickGradeBar
          questionId={row.id}
          grade={grade}
          notes={row.human_grade_notes ?? ""}
          onChange={(g) => setGrade(g)}
        />
      )}
    </li>
  );
}

/** Trainer P2 — promote the exchange into an eval bank. The answer prefills
 *  the expected-answer field so the user's CORRECTION becomes the ground
 *  truth (edit it before saving if the model was wrong). A new question set
 *  can be named here, so the first question never waits on an empty set
 *  the editor will not save. */
const NEW_SET = "\u0000new";

export function AddToBankFlow({
  question,
  answer,
}: {
  question: string;
  answer: string;
}) {
  const auth = useAuth();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const [banks, setBanks] = useState<BankSummary[]>([]);
  const [bankId, setBankId] = useState("");
  const [newName, setNewName] = useState("");
  const [text, setText] = useState(question);
  const [expected, setExpected] = useState(answer);
  const [qtype, setQtype] = useState<QuestionType>("data");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // New exchange → re-seed the form and clear any saved-state message.
  useEffect(() => {
    setText(question);
    setExpected(answer);
    setMsg(null);
  }, [question, answer]);

  if (!canImportData(me.data)) return null;

  const openFlow = async () => {
    const token = auth.user?.id_token;
    if (!token) return;
    setOpen(true);
    const r = await listEvalBanks(token);
    if (r.ok) {
      setBanks(r.data.banks);
      if (!bankId) setBankId(r.data.banks[0]?.id ?? NEW_SET);
    }
  };
  const creating = bankId === NEW_SET;

  const save = async () => {
    const token = auth.user?.id_token;
    if (!token || !bankId || !text.trim() || (creating && !newName.trim())) return;
    setBusy(true);
    setMsg(null);
    let target = bankId;
    if (creating) {
      const made = await createEvalBank({ name: newName.trim() }, token);
      if (!made.ok) {
        setBusy(false);
        setMsg(`The question set was not created: ${made.message}`);
        return;
      }
      target = made.data.id;
      const listed = await listEvalBanks(token);
      if (listed.ok) setBanks(listed.data.banks);
      setBankId(made.data.id);
      setNewName("");
    }
    const r = await appendEvalQuestion(
      target,
      { type: qtype, text: text.trim(), expected_answer: expected.trim() },
      token,
    );
    setBusy(false);
    if (r.ok) {
      setMsg(creating ? "Created the question set and added the question." : "Added to the question set.");
      setOpen(false);
    } else {
      setMsg(
        creating
          ? `The question set was created, but the question was not added: ${r.message}`
          : r.message,
      );
    }
  };

  return (
    <div className="mt-3">
      {!open ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void openFlow()}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
            title="Save this exchange as an eval question — correct the answer first if the model got it wrong"
          >
            Add to a question set…
          </button>
          {msg && <span className="text-xs text-muted-foreground">{msg}</span>}
        </div>
      ) : (
        <div className="space-y-2 rounded-md border border-border bg-card p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Add to a question set
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={bankId}
              onChange={(e) => setBankId(e.target.value)}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              {banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
              <option value={NEW_SET}>New question set…</option>
            </select>
            {creating && (
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Name of the new question set"
                aria-label="Name of the new question set"
                maxLength={200}
                className="min-w-56 flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              />
            )}
            <select
              value={qtype}
              onChange={(e) => setQtype(e.target.value as QuestionType)}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              <option value="data">data</option>
              <option value="ontology">ontology</option>
            </select>
          </div>
          <label className="block">
            <span className="text-xs text-muted-foreground">Question</span>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={2}
              className="mt-1 block w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs text-muted-foreground">
              Expected answer — correct it if the model was wrong; this becomes
              the ground truth
            </span>
            <textarea
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            />
          </label>
          {msg && <div className="text-xs text-destructive">{msg}</div>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || !bankId || !text.trim() || (creating && !newName.trim())}
              className={buttonVariants({ variant: "primary", size: "md" })}
            >
              {busy ? "Saving…" : "Save question"}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function QuickResultPanel({
  result,
  question,
  database,
  model,
}: {
  result: ApiResult<AiChatResponse> | null;
  question: string;
  database: string;
  model: string | null;
}) {
  if (!result) return null;
  if (!result.ok)
    return (
      <ErrorBlock message={result.message} status={result.status} />
    );
  const { answer, trace, usage, latency_ms, stop_reason } = result.data;
  const response = result.data;
  return (
    <div className="space-y-6">
      <AnswerCard answer={answer} stopReason={stop_reason} />
      <div className="flex flex-wrap items-center gap-4">
        <span className="text-xs text-muted-foreground">Answered from {database}</span>
        <AnswerTokenomics response={response} />
        <button
          type="button"
          onClick={() =>
            downloadTraceExport(
              exportOfAnswer({ question, database, graph: null, model, response }),
            )
          }
          className="text-xs text-muted-foreground underline hover:text-foreground"
        >
          Download trace
        </button>
      </div>
      {trace.length > 0 && <TraceList trace={trace} />}
      <MetaFooter
        usage={usage}
        latencyMs={latency_ms}
        stopReason={stop_reason}
        roundtrips={trace.length}
      />
    </div>
  );
}

// =================================================================
// Evaluator — bank list, edit, run, results, grading
// =================================================================

type BankView =
  | { kind: "list" }
  | { kind: "edit"; bankId: string | null /* null = new bank */ }
  | { kind: "run"; bankId: string; runId: string }
  | { kind: "compare"; runA: string; runB: string };

function EvaluatorPanel() {
  const auth = useAuth();
  const me = useMe();
  const canManageBanks = canImportData(me.data); // PowerUser+Admin

  const [view, setView] = useState<BankView>({ kind: "list" });
  const [banks, setBanks] = useState<BankSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reloadBanks = async () => {
    const token = auth.user?.id_token;
    if (!token) {
      setError("Not authenticated");
      return;
    }
    const r = await listEvalBanks(token);
    if (r.ok) {
      setBanks(r.data.banks);
      setError(null);
    } else if (r.status === 503) {
      setError("The evaluator is not enabled on this deployment.");
    } else {
      setError(`Failed to load banks: ${r.message}`);
    }
  };

  useEffect(() => {
    void reloadBanks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user?.id_token]);
  // The banks are the database's: a switch shows that database's banks and
  // leaves any bank, run or comparison that was open.
  useOnDatabaseChange(
    useCallback(() => {
      setView({ kind: "list" });
      setBanks(null);
      void reloadBanks();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  );

  if (error) {
    return (
      <div className="mt-4">
        <ErrorBlock message={error} />
      </div>
    );
  }

  if (view.kind === "edit") {
    return (
      <BankEditView
        bankId={view.bankId}
        onDone={async () => {
          await reloadBanks();
          setView({ kind: "list" });
        }}
        onCancel={() => setView({ kind: "list" })}
      />
    );
  }

  if (view.kind === "run") {
    return (
      <RunView runId={view.runId} onBack={() => setView({ kind: "list" })} />
    );
  }

  if (view.kind === "compare") {
    return (
      <RunCompareView
        runA={view.runA}
        runB={view.runB}
        onBack={() => setView({ kind: "list" })}
      />
    );
  }

  return (
    <BankListView
      banks={banks}
      canManageBanks={canManageBanks}
      onNew={() => setView({ kind: "edit", bankId: null })}
      onEdit={(bankId) => setView({ kind: "edit", bankId })}
      onRun={(bankId, runId) => setView({ kind: "run", bankId, runId })}
      onCompare={(runA, runB) => setView({ kind: "compare", runA, runB })}
      onDeleted={reloadBanks}
    />
  );
}

// The three ways to run a question set: with the ontology in view (mode B),
// against the ontology alone with no model (mode O), or without and with the
// ontology side by side (modes A and B).
type RunAction = "run" | "ontology" | "compare";

const RUN_KIND: Record<EvalMode, string> = {
  A: "Without your ontology",
  B: "With your ontology",
  O: "Ontology only",
};

function BankListView({
  banks,
  canManageBanks,
  onNew,
  onEdit,
  onRun,
  onCompare,
  onDeleted,
}: {
  banks: BankSummary[] | null;
  canManageBanks: boolean;
  onNew: () => void;
  onEdit: (bankId: string) => void;
  onRun: (bankId: string, runId: string) => void;
  onCompare: (runA: string, runB: string) => void;
  onDeleted: () => Promise<void>;
}) {
  const auth = useAuth();
  const [selected, setSelected] = useState<string>("");
  const [busy, setBusy] = useState<RunAction | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [runs, setRuns] = useState<RunListRow[]>([]);
  const [compareSel, setCompareSel] = useState<string[]>([]);

  useEffect(() => {
    if (banks && banks.length > 0 && !selected) {
      const first = banks[0];
      if (first) setSelected(first.id);
    }
  }, [banks, selected]);

  useEffect(() => {
    const token = auth.user?.id_token;
    if (!token || !selected) {
      setRuns([]);
      return;
    }
    void listEvalRuns(selected, token).then((r) => {
      if (r.ok) setRuns(r.data.runs);
      else setRuns([]);
    });
  }, [auth.user?.id_token, selected]);

  const startRun = async (
    mode: EvalMode,
    token: string,
  ): Promise<{ ok: true; runId: string } | { ok: false; message: string }> => {
    const r = await startEvalRun({ bank_id: selected, mode }, token);
    if (r.ok) return { ok: true, runId: r.data.run_id };
    if (r.status === 503) {
      return {
        ok: false,
        message:
          "AI features are not enabled on this deployment. Your administrator can enable them.",
      };
    }
    return {
      ok: false,
      message: `The run did not start: ${r.error}${r.detail ? ` — ${r.detail}` : ""}`,
    };
  };

  const handleRun = async (action: RunAction) => {
    const token = auth.user?.id_token;
    if (!token) {
      setErrorMsg("Sign in to run a question set.");
      return;
    }
    if (!selected) return;
    setBusy(action);
    setErrorMsg(null);
    try {
      if (action === "compare") {
        const without = await startRun("A", token);
        if (!without.ok) {
          setErrorMsg(without.message);
          return;
        }
        const withOntology = await startRun("B", token);
        if (!withOntology.ok) {
          setErrorMsg(
            `The run without your ontology started, but the run with it did not. ${withOntology.message}`,
          );
          return;
        }
        onCompare(without.runId, withOntology.runId);
        return;
      }
      const started = await startRun(action === "ontology" ? "O" : "B", token);
      if (started.ok) onRun(selected, started.runId);
      else setErrorMsg(started.message);
    } finally {
      setBusy(null);
    }
  };

  const selectedBank = banks?.find((b) => b.id === selected) ?? null;
  const runnable = !!selected && (selectedBank?.question_count ?? 0) > 0;

  return (
    <div className="mt-4 space-y-6">
      <p className="max-w-2xl text-sm text-muted-foreground">
        Run a question set through the assistant. Each question is asked the
        way a person would ask it, and the answers are kept, so a run after an
        ontology change can be compared with the run before it.
      </p>

      {banks === null ? (
        <div className="text-sm text-muted-foreground">
          Loading question sets…
        </div>
      ) : banks.length === 0 ? (
        <div className="rounded-md border border-border bg-card p-4 text-sm">
          <div className="font-medium">No question sets yet.</div>
          <div className="mt-1 text-muted-foreground">
            Create a question set to collect acceptance questions for your
            ontology.
          </div>
          {canManageBanks && (
            <button
              type="button"
              onClick={onNew}
              className={cn(
                buttonVariants({ variant: "primary", size: "md" }),
                "mt-3",
              )}
            >
              + New question set
            </button>
          )}
        </div>
      ) : (
        <div className="rounded-md border border-border bg-card p-4">
          <div className="flex flex-wrap items-end gap-4">
            <label className="block">
              <span className="text-sm font-medium">Question set</span>
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="mt-1 block min-w-[16rem] rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {banks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.question_count}{" "}
                    {b.question_count === 1 ? "question" : "questions"})
                  </option>
                ))}
              </select>
            </label>

          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void handleRun("run")}
              disabled={busy !== null || !runnable}
              title="Ask every question through the assistant, with your ontology in view, and keep the answers for grading"
              className={buttonVariants({ variant: "primary", size: "lg" })}
            >
              {busy === "run" ? "Starting…" : "Run questions"}
            </button>
            <button
              type="button"
              onClick={() => void handleRun("ontology")}
              disabled={busy !== null || !runnable}
              title="Check each question against the ontology and the index alone, with no AI model. Free, and quick."
              className={buttonVariants({ variant: "secondary", size: "lg" })}
            >
              {busy === "ontology" ? "Starting…" : "Check ontology only"}
            </button>
            <button
              type="button"
              onClick={() => void handleRun("compare")}
              disabled={busy !== null || !runnable}
              title="Ask every question twice, once without your ontology in view and once with it, and show the two answers side by side"
              className={buttonVariants({ variant: "secondary", size: "lg" })}
            >
              {busy === "compare"
                ? "Starting both runs…"
                : "Compare with and without your ontology"}
            </button>
          </div>

          {selectedBank && selectedBank.question_count === 0 && (
            <div className="mt-2 text-xs text-tres-warning">
              This question set has no questions — add some before running.
            </div>
          )}

          {errorMsg && (
            <div className="mt-3 rounded border border-destructive bg-destructive/10 p-2 text-xs text-destructive">
              {errorMsg}
            </div>
          )}

          {selectedBank && (
            <div className="mt-3 flex gap-2 text-sm">
              <button
                type="button"
                onClick={async () => {
                  const token = auth.user?.id_token;
                  if (!token) return;
                  const r = await downloadBankCorpus(
                    selectedBank.id,
                    selectedBank.name,
                    "jsonl",
                    token,
                  );
                  if (!r.ok) setErrorMsg(`Export failed — ${r.message}`);
                }}
                title="Fine-tune-ready training pairs (.jsonl): one chat-format pair per question — the human-graded-pass answer when one exists, else your authored expected answer"
                className="rounded-md border border-border px-3 py-1.5 font-medium hover:bg-muted"
              >
                Export training set
              </button>
              <button
                type="button"
                onClick={async () => {
                  const token = auth.user?.id_token;
                  if (!token) return;
                  const r = await downloadBankCorpus(
                    selectedBank.id,
                    selectedBank.name,
                    "json",
                    token,
                  );
                  if (!r.ok) setErrorMsg(`Export failed — ${r.message}`);
                }}
                title="Full portable corpus (.json): questions, types, expected answers, verified answers, and the queries that produced them"
                className={buttonVariants({ variant: "secondary", size: "md" })}
              >
                Export corpus (JSON)
              </button>
            </div>
          )}

          {canManageBanks && (
            <div className="mt-3 flex gap-2 text-sm">
              <button
                type="button"
                onClick={onNew}
                className="rounded-md border border-border px-3 py-1.5 hover:bg-muted"
              >
                + New question set
              </button>
              {selectedBank && (
                <>
                  <button
                    type="button"
                    onClick={() => onEdit(selectedBank.id)}
                    className="rounded-md border border-border px-3 py-1.5 hover:bg-muted"
                  >
                    Edit “{selectedBank.name}”
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (
                        !confirm(
                          `Delete bank “${selectedBank.name}”? This also deletes its runs.`,
                        )
                      )
                        return;
                      const token = auth.user?.id_token;
                      if (!token) return;
                      // The set and its runs are gone only if the server says so.
                      const r = await deleteEvalBank(selectedBank.id, token);
                      if (!r.ok) {
                        setErrorMsg(`“${selectedBank.name}” was not deleted: ${r.message}`);
                        return;
                      }
                      setErrorMsg(null);
                      setSelected("");
                      await onDeleted();
                    }}
                    className={buttonVariants({
                      variant: "danger",
                      size: "md",
                    })}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {runs.length > 0 && (
        <div>
          <div className="flex items-center gap-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Recent runs for this question set
            </div>
            {compareSel.length === 2 && compareSel[0] && compareSel[1] && (
              <button
                type="button"
                onClick={() =>
                  onCompare(compareSel[0] as string, compareSel[1] as string)
                }
                className={buttonVariants({ variant: "primary", size: "sm" })}
              >
                Compare selected
              </button>
            )}
          </div>
          <ul className="mt-2 divide-y divide-border rounded-md border border-border bg-card">
            {runs.slice(0, 10).map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
              >
                <label
                  className="flex items-center gap-2"
                  title="Select two runs to compare"
                >
                  <input
                    type="checkbox"
                    checked={compareSel.includes(r.id)}
                    onChange={(e) =>
                      setCompareSel((sel) =>
                        e.target.checked
                          ? [...sel.slice(-1), r.id]
                          : sel.filter((id) => id !== r.id),
                      )
                    }
                  />
                  <span>
                    <span className="font-medium">
                      {r.model_provider}:{r.model_name}
                    </span>{" "}
                    <span className="text-xs text-muted-foreground">
                      {RUN_KIND[r.mode]} · {r.status} ·{" "}
                      {new Date(r.started_at).toLocaleString()}
                      {(r.run_by_email ?? r.run_by) && (
                        <> · by {r.run_by_email ?? r.run_by}</>
                      )}
                    </span>
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => onRun(selected, r.id)}
                  className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
                >
                  View
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// =================================================================
// Run comparison — two runs of the same bank, side by side
// =================================================================

function RunCompareView({
  runA,
  runB,
  onBack,
}: {
  runA: string;
  runB: string;
  onBack: () => void;
}) {
  const auth = useAuth();
  const [detailA, setDetailA] = useState<RunDetail | null>(null);
  const [detailB, setDetailB] = useState<RunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Both runs may still be answering when the comparison opens (Compare with
  // and without your ontology starts them together), so it reads again until
  // neither is running.
  useEffect(() => {
    const token = auth.user?.id_token;
    if (!token) return;
    let timer: number | undefined;
    let stopped = false;
    const load = () => {
      void Promise.all([getEvalRun(runA, token), getEvalRun(runB, token)]).then(
        ([a, b]) => {
          if (stopped) return;
          if (a.ok && b.ok) {
            setDetailA(a.data);
            setDetailB(b.data);
            if (a.data.status === "running" || b.data.status === "running") {
              timer = window.setTimeout(load, 3000);
            }
          } else {
            setError(!a.ok ? a.message : !b.ok ? b.message : "The runs were not loaded.");
          }
        },
      );
    };
    load();
    return () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [auth.user?.id_token, runA, runB]);

  if (error) {
    return (
      <div className="mt-4">
        <button type="button" onClick={onBack} className="text-sm underline">
          ← Back
        </button>
        <ErrorBlock message={error} />
      </div>
    );
  }
  if (!detailA || !detailB) {
    return (
      <div className="mt-4 text-sm text-muted-foreground">
        Loading comparison…
      </div>
    );
  }

  // Align rows by question id; a question present in only one run (bank was
  // edited between runs) still gets a row, matched by snapshot text order.
  const keyOf = (r: RunResultRow) => r.question_id ?? `text:${r.question_text}`;
  const byKeyB = new Map(detailB.results.map((r) => [keyOf(r), r]));
  const seen = new Set<string>();
  const pairs: Array<{ a: RunResultRow | null; b: RunResultRow | null }> = [];
  for (const a of detailA.results) {
    const k = keyOf(a);
    seen.add(k);
    pairs.push({ a, b: byKeyB.get(k) ?? null });
  }
  for (const b of detailB.results) {
    if (!seen.has(keyOf(b))) pairs.push({ a: null, b });
  }

  return (
    <div className="mt-4">
      <button type="button" onClick={onBack} className="text-sm underline">
        ← Back to question sets
      </button>

      <div className="mt-3 grid grid-cols-2 gap-3">
        {[detailA, detailB].map((d, i) => (
          <CompareRunHeader
            key={d.id}
            label={i === 0 ? "First run" : "Second run"}
            run={d}
          />
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {pairs.map(({ a, b }, i) => (
          <div
            key={a?.id ?? b?.id ?? i}
            className="rounded-md border border-border bg-card p-3"
          >
            <div className="text-sm font-medium">{(a ?? b)?.question_text}</div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <CompareAnswerCell result={a} />
              <CompareAnswerCell result={b} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompareRunHeader({ label, run }: { label: string; run: RunDetail }) {
  const graded = run.summary.pass + run.summary.fail + run.summary.partial;
  const score =
    graded > 0 ? (run.summary.pass + 0.5 * run.summary.partial) / graded : null;
  return (
    <div className="rounded-md border border-border bg-card p-3 text-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 font-medium">
        {run.model_provider}:{run.model_name}
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground">
        {RUN_KIND[run.mode]} · {run.status} ·{" "}
        {new Date(run.started_at).toLocaleString()}
      </div>
      <div className="mt-1 text-xs">
        {run.summary.completed}/{run.summary.total} answered · {graded} graded
        {score !== null && (
          <>
            {" "}
            · score{" "}
            <span className="font-medium">{(score * 100).toFixed(0)}%</span>
          </>
        )}
      </div>
    </div>
  );
}

function CompareAnswerCell({ result }: { result: RunResultRow | null }) {
  if (!result) {
    return (
      <div className="text-xs italic text-muted-foreground">
        Not asked in this run (question set edited between runs).
      </div>
    );
  }
  const chip =
    result.human_grade === "pass"
      ? "bg-tres-status-active/15 text-tres-status-active"
      : result.human_grade === "fail"
        ? "bg-destructive/15 text-destructive"
        : result.human_grade === "partial"
          ? "bg-tres-warning/15 text-tres-warning"
          : "bg-muted text-muted-foreground";
  return (
    <div>
      <div className="flex items-center gap-2 text-xs">
        <span className={cn("rounded px-1.5 py-0.5 font-medium", chip)}>
          {result.human_grade ?? "ungraded"}
        </span>
        <span className="text-muted-foreground">
          {(result.latency_ms / 1000).toFixed(1)}s
        </span>
        {result.error_message && (
          <span className="font-medium text-destructive">errored</span>
        )}
      </div>
      <div className="mt-1 text-sm">
        {result.error_message ? (
          <span className="text-destructive">{result.error_message}</span>
        ) : result.answer_text ? (
          <Markdown>{result.answer_text}</Markdown>
        ) : (
          <span className="italic text-muted-foreground">(no answer)</span>
        )}
      </div>
      {result.human_grade_notes && (
        <div className="mt-1 text-xs text-muted-foreground">
          <span className="font-medium">Notes:</span> {result.human_grade_notes}
        </div>
      )}
    </div>
  );
}

// =================================================================
// Bank edit (create + update)
// =================================================================

function BankEditView({
  bankId,
  onDone,
  onCancel,
}: {
  bankId: string | null;
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  const auth = useAuth();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState<EvalQuestion[]>([]);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const token = auth.user?.id_token;
    if (!token) return;
    if (bankId === null) {
      setName("");
      setDescription("");
      setQuestions([
        {
          id: "",
          type: "ontology",
          text: "",
          expected_answer: "",
          unanswerable: false,
        },
      ]);
      return;
    }
    void getEvalBank(bankId, token).then((r) => {
      if (r.ok) {
        const bd: BankDetail = r.data;
        setName(bd.name);
        setDescription(bd.description);
        setQuestions(
          bd.questions.length > 0
            ? bd.questions
            : [
                {
                  id: "",
                  type: "ontology",
                  text: "",
                  expected_answer: "",
                  unanswerable: false,
                },
              ],
        );
      } else {
        setErrorMsg(`Failed to load bank: ${r.message}`);
      }
    });
  }, [auth.user?.id_token, bankId]);

  const addQuestion = () => {
    setQuestions((qs) => [
      ...qs,
      {
        id: "",
        type: "ontology",
        text: "",
        expected_answer: "",
        unanswerable: false,
      },
    ]);
  };

  const removeQuestion = (idx: number) => {
    setQuestions((qs) => qs.filter((_, i) => i !== idx));
  };

  const updateQuestion = (idx: number, patch: Partial<EvalQuestion>) => {
    setQuestions((qs) =>
      qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)),
    );
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importMsg, setImportMsg] = useState<{ text: string; failed: boolean } | null>(null);

  const meData = useMe().data;
  const readQuestions = async (
    file: File,
  ): Promise<{ ok: true; questions: EvalQuestion[] } | { ok: false; error: string }> => {
    if (!isSheetFile(file.name)) {
      return { ok: true, questions: parseQuestionsFile(await file.text()) };
    }
    const token = auth.user?.id_token;
    if (!token) return { ok: false, error: "Sign in to read a spreadsheet." };
    if (isQros(meData)) {
      const r = await profileSpreadsheet(file, token);
      if (!r.ok) return { ok: false, error: r.detail ?? r.error };
      const { columns, rows } = r.data;
      return {
        ok: true,
        questions: questionsFromTable(
          columns,
          rows.map((row) => columns.map((c) => row[c] ?? "")),
        ),
      };
    }
    const r = await rdfProfileSpreadsheet(file, token);
    if (!r.ok) return { ok: false, error: r.detail ?? r.error };
    return { ok: true, questions: questionsFromTable(r.data.columns, r.data.rows) };
  };

  const handleImportFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportMsg({ text: `Reading ${file.name}…`, failed: false });
      const read = await readQuestions(file);
      if (!read.ok) {
        setImportMsg({ text: `${file.name} could not be read: ${read.error}`, failed: true });
      } else if (read.questions.length === 0) {
        setImportMsg({
          text: `No questions found in ${file.name}. Write each question on a line starting Q: and its answer on a line starting A:.`,
          failed: true,
        });
      } else {
        const parsed = read.questions;
        const answered = parsed.filter((q) => q.expected_answer).length;
        // Drop the empty placeholder row(s), append the imported questions.
        setQuestions((qs) => [
          ...qs.filter((q) => q.text.trim() !== ""),
          ...parsed,
        ]);
        setImportMsg({
          text: `Imported ${parsed.length} question${parsed.length === 1 ? "" : "s"}, ${
            answered === parsed.length
              ? "each with its expected answer"
              : `${answered} with an expected answer`
          }. Review them and press Save.`,
          failed: false,
        });
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = ""; // allow re-importing the same file
  };

  const handleSave = async () => {
    const token = auth.user?.id_token;
    if (!token) {
      setErrorMsg("Not authenticated");
      return;
    }
    if (!name.trim()) {
      setErrorMsg("A question set needs a name.");
      return;
    }
    const cleaned: Array<{
      id?: string;
      type: QuestionType;
      text: string;
      expected_answer?: string;
      unanswerable?: boolean;
    }> = questions
      .map((q) => {
        const expected = q.expected_answer.trim();
        const base: {
          id?: string;
          type: QuestionType;
          text: string;
          expected_answer?: string;
          unanswerable?: boolean;
        } = {
          type: q.type,
          text: q.text.trim(),
          unanswerable: q.unanswerable,
        };
        if (q.id) base.id = q.id;
        if (expected) base.expected_answer = expected;
        return base;
      })
      .filter((q) => q.text.length > 0);
    if (cleaned.length === 0) {
      setErrorMsg("At least one question with text is required.");
      return;
    }
    setBusy(true);
    setErrorMsg(null);
    try {
      let id = bankId;
      if (id === null) {
        const r = await createEvalBank(
          { name: name.trim(), description: description.trim() },
          token,
        );
        if (!r.ok) {
          setErrorMsg(
            `Create failed: ${r.message}`,
          );
          return;
        }
        id = r.data.id;
      } else {
        const r = await updateEvalBank(
          id,
          { name: name.trim(), description: description.trim() },
          token,
        );
        if (!r.ok) {
          setErrorMsg(
            `Update failed: ${r.message}`,
          );
          return;
        }
      }
      const r = await replaceEvalQuestions(id, cleaned, token);
      if (!r.ok) {
        setErrorMsg(
          `Saving questions failed: ${r.message}`,
        );
        return;
      }
      await onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-md border border-border bg-card p-4 space-y-3">
        <div className="flex gap-3">
          <label className="block flex-1">
            <span className="text-sm font-medium">Question set name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="e.g. Nova acceptance v1"
            />
          </label>
        </div>
        <label className="block">
          <span className="text-sm font-medium">Description (optional)</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="What this question set measures"
          />
        </label>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Questions ({questions.length})
        </div>
        {questions.map((q, idx) => (
          <div
            key={idx}
            className="rounded-md border border-border bg-card p-3"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">#{idx + 1}</span>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    checked={q.unanswerable}
                    onChange={(e) =>
                      updateQuestion(idx, { unanswerable: e.target.checked })
                    }
                    className="rounded border-input"
                  />
                  Unanswerable
                </label>
                <select
                  value={q.type}
                  onChange={(e) =>
                    updateQuestion(idx, {
                      type: e.target.value as QuestionType,
                    })
                  }
                  className="rounded border border-input bg-background px-2 py-1 text-xs"
                >
                  <option value="ontology">ontology — schema coverage</option>
                  <option value="data">data — end-to-end grounding</option>
                </select>
                <button
                  type="button"
                  onClick={() => removeQuestion(idx)}
                  className={buttonVariants({ variant: "danger", size: "sm" })}
                  title="Remove question"
                >
                  Remove
                </button>
              </div>
            </div>
            {q.unanswerable && (
              <div className="mt-1 text-xs text-muted-foreground">
                Correct answer is a decline — used for the hallucination check
              </div>
            )}
            <label className="mt-2 block">
              <span className="text-xs text-muted-foreground">
                Question text
              </span>
              <textarea
                value={q.text}
                onChange={(e) => updateQuestion(idx, { text: e.target.value })}
                rows={2}
                className="mt-1 block w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                placeholder={
                  q.type === "ontology"
                    ? "e.g. Assuming the data exists, can I get a list of approved models for video production work?"
                    : "e.g. Which models are approved for video production work?"
                }
              />
            </label>
            <label className="mt-2 block">
              <span className="text-xs text-muted-foreground">
                Expected answer (free text)
              </span>
              <textarea
                value={q.expected_answer}
                onChange={(e) =>
                  updateQuestion(idx, { expected_answer: e.target.value })
                }
                rows={2}
                className="mt-1 block w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                placeholder="What a correct answer looks like — used by the human reviewer at grading time."
              />
            </label>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={addQuestion}
            className={cn(
              buttonVariants({ variant: "secondary", size: "md" }),
              "border-dashed",
            )}
          >
            + Add question
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="rounded-md border border-dashed border-border px-3 py-1.5 text-sm hover:bg-muted"
            title="Add questions from a file you already have. A text or Markdown file: Q: and A: lines, or a question followed by its answer paragraph, or **Q1. …** headings with the answer below; a file of questions alone, one per line, adds them without answers. A spreadsheet (.csv or .xlsx): a Question column and an Expected answer column."
          >
            Import questions from a file
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".md,.markdown,.txt,.csv,.xlsx,.xls,text/plain,text/markdown,text/csv"
            onChange={handleImportFile}
            className="hidden"
          />
          {importMsg &&
            (importMsg.failed ? (
              <Message kind="warning" density="compact">
                {importMsg.text}
              </Message>
            ) : (
              <span className="text-xs text-muted-foreground">
                {importMsg.text}
              </span>
            ))}
        </div>
      </div>

      {errorMsg && (
        <div className="rounded border border-destructive bg-destructive/10 p-2 text-xs text-destructive">
          {errorMsg}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={busy}
          className={buttonVariants({ variant: "primary", size: "lg" })}
        >
          {busy ? "Saving…" : "Save question set"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className={buttonVariants({ variant: "secondary", size: "lg" })}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// =================================================================
// Run view — poll results, grade per-question
// =================================================================

function RunView({ runId, onBack }: { runId: string; onBack: () => void }) {
  const auth = useAuth();
  const [run, setRun] = useState<RunDetail | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [stopping, setStopping] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const token = auth.user?.id_token;
      if (!token) return;
      const r = await getEvalRun(runId, token);
      if (cancelled) return;
      if (r.ok) {
        setRun(r.data);
      } else {
        setErrorMsg(`Failed to load run: ${r.message}`);
      }
    };
    void tick();
    const interval = window.setInterval(() => {
      if (
        run?.status === "completed" ||
        run?.status === "failed" ||
        run?.status === "cancelled"
      )
        return;
      void tick();
    }, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, auth.user?.id_token, run?.status]);

  const handleStop = async () => {
    const token = auth.user?.id_token;
    if (!token) return;
    setStopping(true);
    const r = await cancelEvalRun(runId, token);
    setStopping(false);
    if (!r.ok) setErrorMsg(`Failed to cancel run: ${r.message}`);
    // The poll picks up status='cancelled'; partial results already in remain.
  };

  if (errorMsg) {
    return (
      <div className="mt-4">
        <ErrorBlock message={errorMsg} />
        <button
          type="button"
          onClick={onBack}
          className={cn(
            buttonVariants({ variant: "secondary", size: "md" }),
            "mt-3",
          )}
        >
          ← Back
        </button>
      </div>
    );
  }
  if (!run) {
    return (
      <div className="mt-4 text-sm text-muted-foreground">Loading run…</div>
    );
  }

  return (
    <div className="mt-4 space-y-6">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className={buttonVariants({ variant: "secondary", size: "md" })}
        >
          ← Back to question sets
        </button>
        <div className="flex items-center gap-3">
          {run.status === "running" && (
            <button
              type="button"
              onClick={handleStop}
              disabled={stopping}
              className={buttonVariants({ variant: "danger", size: "md" })}
            >
              {stopping ? "Stopping…" : "Stop run"}
            </button>
          )}
          <div className="text-xs text-muted-foreground">
            {run.model_provider}:{run.model_name} · {RUN_KIND[run.mode]} ·{" "}
            {run.status}
            {run.completed_at
              ? ` · ended ${new Date(run.completed_at).toLocaleString()}`
              : ""}
          </div>
        </div>
      </div>

      <RunSummaryCard run={run} />
      {run.status !== "running" && (
        <RecommendationsCard
          runId={run.id}
          runStatus={run.status}
          gradeStamp={run.results.map((r) => r.human_grade ?? "-").join(",")}
          results={run.results}
        />
      )}

      <div className="space-y-3">
        {run.results.map((r) => (
          <ResultCard
            key={r.id}
            runId={run.id}
            result={r}
            onChange={(updated) => {
              setRun(
                (prev) =>
                  prev && {
                    ...prev,
                    results: prev.results.map((row) =>
                      row.id === updated.id ? updated : row,
                    ),
                    summary: recomputeSummary(
                      prev.results.map((row) =>
                        row.id === updated.id ? updated : row,
                      ),
                      prev.summary.total,
                    ),
                  },
              );
            }}
          />
        ))}
      </div>

      {run.status === "running" && (
        <div className="text-xs text-muted-foreground">
          Run still executing — results stream in as each question completes
          (polled every 2 s).
        </div>
      )}
    </div>
  );
}

function recomputeSummary(results: RunResultRow[], total: number) {
  let pass = 0,
    fail = 0,
    partial = 0,
    ungraded = 0,
    errored = 0;
  for (const r of results) {
    if (r.error_message) errored++;
    switch (r.human_grade) {
      case "pass":
        pass++;
        break;
      case "fail":
        fail++;
        break;
      case "partial":
        partial++;
        break;
      default:
        ungraded++;
    }
  }
  return {
    total,
    completed: results.length,
    pass,
    fail,
    partial,
    ungraded,
    errored,
  };
}

const LEVER_LABEL: Record<EvalRecommendation["lever"], string> = {
  config: "Configuration",
  model: "Model",
  methodology: "Methodology",
  data: "Data",
};

function truncateQuestion(text: string): string {
  return text.length > 90 ? `${text.slice(0, 90).trimEnd()}…` : text;
}

function QuestionRefList({ items }: { items: EvalQuestionRef[] }) {
  return (
    <ul className="space-y-0.5">
      {items.map((q) => (
        <li key={q.result_id} className="text-xs text-muted-foreground">
          <span className="font-mono">Q{q.ordering + 1}</span> —{" "}
          {truncateQuestion(q.question_text)}
        </li>
      ))}
    </ul>
  );
}

function DeltaGroup({
  label,
  items,
  accentBorder,
  accentText,
}: {
  label: string;
  items: EvalQuestionRef[];
  accentBorder: string;
  accentText: string;
}) {
  if (items.length === 0) return null;
  return (
    <div className={cn("border-l-2 pl-2", accentBorder)}>
      <div className={cn("text-xs font-medium", accentText)}>{label}</div>
      <div className="mt-0.5">
        <QuestionRefList items={items} />
      </div>
    </div>
  );
}

/** Trainer P1: the run's telemetry turned into "what to do" — budget, model,
 *  methodology, and data-lever advice computed server-side, plus the delta
 *  against the prior comparable run and the curation work list. Grading
 *  results refines it, so it refetches whenever the run status or any grade
 *  changes (gradeStamp fingerprints the grades). */
function RecommendationsCard({
  runId,
  runStatus,
  gradeStamp,
  results,
}: {
  runId: string;
  runStatus: string;
  gradeStamp: string;
  results: RunResultRow[];
}) {
  const auth = useAuth();
  const [data, setData] = useState<RunRecommendationsResponse | null>(null);

  useEffect(() => {
    const token = auth.user?.id_token;
    if (!token) return;
    let cancelled = false;
    void getRunRecommendations(runId, token).then((r) => {
      if (!cancelled && r.ok) setData(r.data);
    });
    return () => {
      cancelled = true;
    };
  }, [runId, runStatus, gradeStamp, auth.user?.id_token]);

  if (!data) return null;
  const { recommendations: recs, work_list: work, delta } = data;
  const hasWork =
    work.retrieval_empty.length > 0 ||
    work.retrieval_empty_unnamed.length > 0 ||
    work.wrong_despite_hit.length > 0 ||
    work.never_asked.length > 0;
  const lookHere = work.precheck.filter((p) => p.verdict === "look_here");
  const likelyPassCount = work.precheck.length - lookHere.length;
  if (!delta && !hasWork && work.precheck.length === 0 && recs.length === 0)
    return null;

  const orderingById = new Map(results.map((r) => [r.id, r.ordering]));
  const lookHereLabels = lookHere
    .map((p) => orderingById.get(p.result_id))
    .filter((o): o is number => o !== undefined)
    .sort((a, b) => a - b)
    .map((o) => `Q${o + 1}`);

  return (
    <div className="rounded-md border border-border bg-card p-4 space-y-4">
      {delta && (
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Since last run
          </div>
          <div className="mt-2 space-y-2">
            <div className="relative">
              <DeltaGroup
                label="Fixed since last run"
                items={delta.fixed}
                accentBorder="border-tres-status-active"
                accentText="text-tres-status-active"
              />
              <CelebrationBurst
                fireKey={
                  delta.fixed.length > 0
                    ? `${runId}:${delta.fixed.length}`
                    : null
                }
              />
            </div>
            <DeltaGroup
              label="Broke since last run"
              items={delta.broke}
              accentBorder="border-destructive"
              accentText="text-destructive"
            />
            <DeltaGroup
              label="Still failing"
              items={delta.still_failing}
              accentBorder="border-tres-warning"
              accentText="text-tres-warning"
            />
            {delta.not_comparable > 0 && (
              <div className="text-xs text-muted-foreground">
                {delta.not_comparable} not comparable (ungraded)
              </div>
            )}
          </div>
        </div>
      )}

      {hasWork && (
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Work list
          </div>
          {/* Where a question's place on this list comes from: nobody has
              graded these, so the page must not read as a verdict (UAT
              2026-10-05, "Where does this verdict come from?"). */}
          <p className="mt-1 text-xs text-muted-foreground">
            A question is listed here when you graded its answer fail or partial, or, before any grade, when fewer
            than eight in ten of the words and numbers of its expected answer appear in the answer given. That
            comparison is a pointer to what to grade first. It is not a grade, and a right answer in other words is
            listed too.
          </p>
          <div className="mt-2 space-y-3">
            {(work.retrieval_empty.length > 0 ||
              work.retrieval_empty_unnamed.length > 0) && (
              <div>
                <div className="text-sm font-medium">
                  Retrieval found nothing
                </div>
                <div className="mt-1 space-y-2">
                  {work.retrieval_empty.map((g) => (
                    <div key={g.term}>
                      <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                        {g.term}
                      </span>
                      {g.readiness && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {g.readiness}
                        </p>
                      )}
                      <div className="mt-1">
                        <QuestionRefList items={g.questions} />
                      </div>
                    </div>
                  ))}
                  {work.retrieval_empty_unnamed.length > 0 && (
                    <QuestionRefList items={work.retrieval_empty_unnamed} />
                  )}
                </div>
              </div>
            )}
            {work.wrong_despite_hit.length > 0 && (
              <div>
                <div className="text-sm font-medium">
                  The tools returned data, and the answer does not match the expected one
                </div>
                <div className="mt-1">
                  <QuestionRefList items={work.wrong_despite_hit} />
                </div>
              </div>
            )}
            {work.never_asked.length > 0 && (
              <div>
                <div className="text-sm font-medium">
                  Never attempted retrieval
                </div>
                <div className="mt-1">
                  <QuestionRefList items={work.never_asked} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {work.precheck.length > 0 && (
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Grading order hint
          </div>
          <div className="mt-1 text-xs">
            {lookHereLabels.length > 0 && (
              <span className="font-medium">
                Start here: {lookHereLabels.join(", ")}
              </span>
            )}
            {likelyPassCount > 0 && (
              <span className="text-muted-foreground">
                {lookHereLabels.length > 0 ? " · " : ""}
                {likelyPassCount} likely pass
              </span>
            )}
          </div>
        </div>
      )}

      {recs.length > 0 && (
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Recommendations
          </div>
          <div className="mt-2 space-y-3">
            {recs.map((rec, i) => (
              <div
                key={i}
                className={cn(
                  "flex items-start gap-2",
                  rec.severity === "warn" &&
                    "border-l-2 border-tres-warning pl-2",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase",
                    rec.lever === "config" && "bg-muted text-foreground",
                    rec.lever === "model" &&
                      "bg-tres-action/15 text-tres-action",
                    rec.lever === "methodology" &&
                      "bg-tres-warning/15 text-tres-warning",
                    rec.lever === "data" &&
                      "bg-tres-status-active/15 text-tres-status-active",
                  )}
                >
                  {LEVER_LABEL[rec.lever]}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium">{rec.title}</div>
                  <p className="text-xs text-muted-foreground">{rec.detail}</p>
                  {rec.evidence.length > 0 && (
                    <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground/80">
                      {rec.evidence.map((e, j) => (
                        <li key={j}>{e}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RunSummaryCard({ run }: { run: RunDetail }) {
  const s = run.summary;
  const graded = s.pass + s.fail + s.partial;
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="grid gap-6 sm:grid-cols-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Model run
          </div>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <Stat label="Answered" value={`${s.completed} / ${s.total}`} />
            <Stat
              label="Errored"
              value={s.errored}
              tone={s.errored > 0 ? "error" : "muted"}
            />
          </div>
        </div>
        <div className="sm:col-span-2 sm:border-l sm:border-border sm:pl-6">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Your review
          </div>
          {graded === 0 ? (
            <p className="mt-2 max-w-md text-sm text-muted-foreground">
              Nothing graded yet — grade each answer below and the pass/fail
              tally and run score appear here.
            </p>
          ) : (
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-5">
              <Stat label="Graded" value={`${graded} / ${s.completed}`} />
              <Stat label="Pass" value={s.pass} tone="success" />
              <Stat label="Fail" value={s.fail} tone="error" />
              <Stat label="Partial" value={s.partial} tone="warn" />
              <Stat
                label="Score"
                value={`${Math.round(((s.pass + s.partial * 0.5) / graded) * 100)}%`}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "muted",
}: {
  label: string;
  value: number | string;
  tone?: "muted" | "success" | "error" | "warn";
}) {
  const toneCls =
    tone === "success"
      ? "text-tres-status-active"
      : tone === "error"
        ? "text-destructive"
        : tone === "warn"
          ? "text-tres-warning"
          : "text-foreground";
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("text-lg font-semibold", toneCls)}>{value}</div>
    </div>
  );
}

function ResultCard({
  runId,
  result,
  onChange,
}: {
  runId: string;
  result: RunResultRow;
  onChange: (r: RunResultRow) => void;
}) {
  const auth = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [showTokenomics, setShowTokenomics] = useState(false);
  const [savingGrade, setSavingGrade] = useState<HumanGrade | null>(null);
  const [gradeError, setGradeError] = useState<string | null>(null);
  const [celebrate, setCelebrate] = useState<string | null>(null);
  const [notes, setNotes] = useState(result.human_grade_notes ?? "");
  const [savingNotes, setSavingNotes] = useState(false);

  const trace = parseTrace(result.trace_json);

  const setGrade = async (grade: HumanGrade | null) => {
    const token = auth.user?.id_token;
    if (!token) return;
    setSavingGrade(grade);
    const body: { human_grade: HumanGrade | null; human_grade_notes?: string } =
      {
        human_grade: grade,
      };
    if (notes) body.human_grade_notes = notes;
    let saved = false;
    try {
      const r = await gradeEvalResult(runId, result.id, body, token);
      saved = r.ok;
    } catch {
      saved = false;
    }
    setSavingGrade(null);
    if (saved) {
      setGradeError(null);
      if (grade === "pass") {
        setCelebrate(`${result.id}:${Date.now()}`);
      }
      onChange({
        ...result,
        human_grade: grade,
        graded_at: new Date().toISOString(),
      });
    } else {
      setGradeError("Grade failed to save — retry");
    }
  };

  const saveNotes = async () => {
    const token = auth.user?.id_token;
    if (!token) return;
    setSavingNotes(true);
    let saved = false;
    try {
      const r = await gradeEvalResult(
        runId,
        result.id,
        { human_grade: result.human_grade ?? null, human_grade_notes: notes },
        token,
      );
      saved = r.ok;
    } catch {
      saved = false;
    }
    setSavingNotes(false);
    if (saved) {
      setGradeError(null);
      onChange({ ...result, human_grade_notes: notes });
    } else {
      setGradeError("Grade failed to save — retry");
    }
  };

  const gradeColor =
    result.human_grade === "pass"
      ? "border-tres-status-active bg-tres-status-active/5"
      : result.human_grade === "fail"
        ? "border-destructive/40 bg-destructive/5"
        : result.human_grade === "partial"
          ? "border-tres-warning bg-tres-warning/5"
          : "border-border bg-card";

  return (
    <div className={cn("rounded-md border p-3", gradeColor)}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
              {result.ordering + 1}
            </span>
            <span className="text-xs uppercase text-muted-foreground">
              {result.question_type}
            </span>
            {result.error_message && (
              <span className="text-xs font-medium text-destructive">
                errored
              </span>
            )}
          </div>
          <div className="mt-1 text-sm font-medium">{result.question_text}</div>
          {result.expected_answer && (
            <div className="mt-1 text-xs text-muted-foreground">
              <span className="font-medium">Expected:</span>{" "}
              {result.expected_answer}
            </div>
          )}
        </div>
        <div className="relative flex gap-1">
          <CelebrationBurst fireKey={celebrate} />
          {(["pass", "partial", "fail"] as HumanGrade[]).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => void setGrade(g)}
              disabled={savingGrade !== null}
              className={cn(
                "rounded px-2 py-1 text-xs font-medium border",
                result.human_grade === g
                  ? g === "pass"
                    ? "border-tres-status-active bg-tres-status-active text-primary-foreground"
                    : g === "fail"
                      ? "border-destructive bg-destructive text-primary-foreground"
                      : "border-tres-warning bg-tres-warning text-primary-foreground"
                  : "border-border hover:bg-muted",
              )}
            >
              {g}
            </button>
          ))}
          {result.human_grade && (
            <button
              type="button"
              onClick={() => void setGrade(null)}
              className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
              title="Ungrade"
            >
              ×
            </button>
          )}
        </div>
      </div>
      {gradeError && (
        <div className="mt-1 text-right text-xs text-destructive">
          {gradeError}
        </div>
      )}

      <div className="mt-2">
        <div className="text-xs font-medium text-muted-foreground">
          Model's answer
        </div>
        <div className="mt-1 text-sm">
          {result.error_message ? (
            <span className="text-destructive">{result.error_message}</span>
          ) : result.answer_text ? (
            <Markdown>{result.answer_text}</Markdown>
          ) : (
            <span className="italic text-muted-foreground">(no answer)</span>
          )}
        </div>
      </div>

      <div className="mt-2">
        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">
            Reviewer notes
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => {
              if (notes !== (result.human_grade_notes ?? "")) void saveNotes();
            }}
            rows={2}
            placeholder="Why this grade? Anything missing from the answer?"
            className="mt-1 block w-full rounded border border-input bg-background px-2 py-1 text-xs"
          />
        </label>
        {savingNotes && (
          <div className="mt-1 text-xs text-muted-foreground">Saving…</div>
        )}
      </div>

      <div className="mt-2 flex gap-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-muted-foreground underline hover:text-foreground"
        >
          {expanded ? "Hide trace" : "Show trace + generated query"}
        </button>
        <button
          type="button"
          aria-expanded={showTokenomics}
          onClick={() => setShowTokenomics((v) => !v)}
          className="text-xs text-muted-foreground underline hover:text-foreground"
        >
          Tokenomics
        </button>
      </div>
      {showTokenomics && <TokenomicsPanel row={result} />}
      {expanded && (
        <div className="mt-2 space-y-2">
          {result.generated_sparql && (
            <div>
              <div className="text-xs font-medium text-muted-foreground">
                Final generated query
              </div>
              <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words rounded bg-background p-2 font-mono text-xs">
                {result.generated_sparql}
              </pre>
            </div>
          )}
          {trace.length > 0 && <TraceList trace={trace} />}
          <div className="text-xs text-muted-foreground">
            {result.latency_ms.toLocaleString()} ms · {trace.length} tool{" "}
            {trace.length === 1 ? "roundtrip" : "roundtrips"} ·{" "}
            {result.input_tokens.toLocaleString()} in /{" "}
            {result.output_tokens.toLocaleString()} out tokens
            {result.stop_reason ? ` · stop: ${result.stop_reason}` : ""}
          </div>
        </div>
      )}
    </div>
  );
}

function parseTrace(trace_json: string): AiChatTraceStep[] {
  try {
    const parsed = JSON.parse(trace_json);
    return Array.isArray(parsed) ? (parsed as AiChatTraceStep[]) : [];
  } catch {
    return [];
  }
}

// =================================================================
// Shared sub-components (used by both Quick + Bank result views)
// =================================================================

function ErrorBlock({ message, status }: { message: string; status?: number }) {
  return (
    <Message kind="warning" className="whitespace-pre-wrap p-4">
      {message}
      {status === 503 && (
        <div className="mt-2 text-xs">
          AI features are not enabled on this deployment. Your administrator can
          enable them.
        </div>
      )}
    </Message>
  );
}

function AnswerCard({
  answer,
  stopReason,
}: {
  answer: string;
  stopReason: string;
}) {
  const stoppedEarly =
    stopReason === "max_roundtrips" || stopReason === "token_budget";
  const tone = stoppedEarly
    ? "border-tres-warning bg-tres-warning/10"
    : "border-border bg-card";
  const heading = stoppedEarly ? "Stopped before answering" : "Answer";
  return (
    <div className={cn("rounded-md border p-4", tone)}>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {heading}
      </div>
      <div className="mt-2 text-sm leading-relaxed">
        {answer ? (
          <Markdown>{answer}</Markdown>
        ) : (
          <span className="italic text-muted-foreground">(no answer)</span>
        )}
      </div>
    </div>
  );
}

function TraceList({ trace }: { trace: AiChatTraceStep[] }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Trace ({trace.length} {trace.length === 1 ? "step" : "steps"})
      </div>
      <ol className="mt-2 space-y-3">
        {trace.map((step, idx) => (
          <li key={idx}>
            <TraceStepCard step={step} index={idx + 1} />
          </li>
        ))}
      </ol>
    </div>
  );
}

function TraceStepCard({
  step,
  index,
}: {
  step: AiChatTraceStep;
  index: number;
}) {
  if (step.kind === "generated_query") {
    return (
      <div className="rounded-md border border-border bg-muted/30 p-3">
        <StepHeader index={index} label="Generated query" tone="info" />
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded bg-background p-2 font-mono text-xs">
          {step.sparql}
        </pre>
      </div>
    );
  }
  if (step.kind === "query_result") {
    return (
      <div className="rounded-md border border-border bg-card p-3">
        <StepHeader
          index={index}
          label={`Query result — ${step.rows.length} row${step.rows.length === 1 ? "" : "s"}${
            step.truncated ? " (truncated to 20)" : ""
          }`}
          tone="success"
        />
        <ResultTable rows={step.rows} />
      </div>
    );
  }
  if (step.kind === "tool_result") {
    return (
      <div className="rounded-md border border-border bg-card p-3">
        <StepHeader
          index={index}
          label={`Tool result — ${step.tool}`}
          tone="info"
        />
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded bg-background p-2 font-mono text-xs">
          {step.text}
        </pre>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
      <StepHeader index={index} label="Query error" tone="error" />
      {step.sparql && (
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded bg-background p-2 font-mono text-xs">
          {step.sparql}
        </pre>
      )}
      <div className="mt-2 whitespace-pre-wrap text-xs text-destructive">
        {step.message}
      </div>
    </div>
  );
}

function StepHeader({
  index,
  label,
  tone,
}: {
  index: number;
  label: string;
  tone: "info" | "success" | "error";
}) {
  const toneClass =
    tone === "success"
      ? "text-tres-status-active"
      : tone === "error"
        ? "text-destructive"
        : "text-muted-foreground";
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
        {index}
      </span>
      <span className={cn("font-medium", toneClass)}>{label}</span>
    </div>
  );
}

function ResultTable({ rows }: { rows: Array<Record<string, string>> }) {
  if (rows.length === 0) {
    return (
      <div className="mt-2 text-xs italic text-muted-foreground">
        No rows returned.
      </div>
    );
  }
  const cols = Array.from(
    rows.reduce((acc, row) => {
      Object.keys(row).forEach((k) => acc.add(k));
      return acc;
    }, new Set<string>()),
  );
  return (
    <div className="mt-2 overflow-x-auto rounded border border-border">
      <table className="min-w-full text-xs">
        <thead className="border-b border-border bg-muted/50">
          <tr>
            {cols.map((c) => (
              <th key={c} className="px-2 py-1.5 text-left font-medium">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b border-border last:border-b-0">
              {cols.map((c) => (
                <td key={c} className="px-2 py-1 font-mono">
                  {row[c] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MetaFooter({
  usage,
  latencyMs,
  stopReason,
  roundtrips,
}: {
  usage: AiChatResponse["usage"];
  latencyMs: number;
  stopReason: string;
  roundtrips: number;
}) {
  const parts: string[] = [];
  parts.push(`${latencyMs.toLocaleString()} ms`);
  parts.push(
    `${roundtrips} tool ${roundtrips === 1 ? "roundtrip" : "roundtrips"}`,
  );
  if (usage) {
    parts.push(
      `${usage.input_tokens.toLocaleString()} in / ${usage.output_tokens.toLocaleString()} out tokens`,
    );
  }
  parts.push(`stop: ${stopReason}`);
  return (
    <div className="text-xs text-muted-foreground">{parts.join(" · ")}</div>
  );
}
